import { Task, ActionItem } from '@/types/project';

export interface ParsedCSVRow {
  taskName: string;
  intent?: string;
  specs?: string[];
  duration?: number; // in minutes
  startDate?: Date;
  endDate?: Date;
  isFixedDeadline?: boolean;
  dependencies?: string[];
}

export interface CSVParseResult {
  rows: ParsedCSVRow[];
  hasDateColumns: boolean;
  errors: string[];
}

// Parse CSV text into rows
export function parseCSV(csvText: string): string[][] {
  const lines = csvText.trim().split(/\r?\n/);
  const rows: string[][] = [];
  
  for (const line of lines) {
    // Handle quoted fields with commas
    const row: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          currentField += '"';
          i++; // Skip escaped quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
    }
    row.push(currentField.trim());
    rows.push(row);
  }
  
  return rows;
}

// Map column headers to known fields
function mapColumnIndex(headers: string[]): Record<string, number> {
  const mapping: Record<string, number> = {};
  
  const patterns: Record<string, RegExp[]> = {
    taskName: [/^task\s*name$/i, /^name$/i, /^title$/i, /^task$/i],
    intent: [/^intent$/i, /^purpose$/i, /^goal$/i, /^objective$/i, /^description$/i],
    specs: [/^specs?$/i, /^specifications?$/i, /^requirements?$/i, /^details?$/i],
    duration: [/^duration$/i, /^time$/i, /^hours?$/i, /^length$/i, /^estimate$/i],
    startDate: [/^start\s*date$/i, /^start$/i, /^begin$/i, /^from$/i],
    endDate: [/^end\s*date$/i, /^end$/i, /^due$/i, /^deadline$/i, /^to$/i],
    isFixedDeadline: [/^fixed$/i, /^hard\s*deadline$/i, /^locked$/i],
    dependencies: [/^dependencies?$/i, /^depends\s*on$/i, /^after$/i, /^predecessors?$/i],
  };
  
  headers.forEach((header, index) => {
    const normalizedHeader = header.trim();
    
    for (const [field, fieldPatterns] of Object.entries(patterns)) {
      if (fieldPatterns.some(pattern => pattern.test(normalizedHeader))) {
        mapping[field] = index;
        break;
      }
    }
  });
  
  return mapping;
}

// Parse duration string to minutes
function parseDuration(durationStr: string): number | undefined {
  if (!durationStr) return undefined;
  
  const normalized = durationStr.toLowerCase().trim();
  
  // "2 hours", "2h", "2 hrs"
  const hoursMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)$/);
  if (hoursMatch) {
    return Math.round(parseFloat(hoursMatch[1]) * 60);
  }
  
  // "30 minutes", "30m", "30 min"
  const minutesMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)$/);
  if (minutesMatch) {
    return Math.round(parseFloat(minutesMatch[1]));
  }
  
  // "1 day", "1d"
  const daysMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:days?|d)$/);
  if (daysMatch) {
    return Math.round(parseFloat(daysMatch[1]) * 8 * 60); // 8-hour workday
  }
  
  // Just a number - assume hours
  const numMatch = normalized.match(/^(\d+(?:\.\d+)?)$/);
  if (numMatch) {
    return Math.round(parseFloat(numMatch[1]) * 60);
  }
  
  return undefined;
}

// Parse date string
function parseDate(dateStr: string): Date | undefined {
  if (!dateStr) return undefined;
  
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  return undefined;
}

// Main CSV parsing function
export function parseCSVToTasks(csvText: string): CSVParseResult {
  const errors: string[] = [];
  const rawRows = parseCSV(csvText);
  
  if (rawRows.length < 2) {
    return { rows: [], hasDateColumns: false, errors: ['CSV must have a header row and at least one data row'] };
  }
  
  const headers = rawRows[0];
  const dataRows = rawRows.slice(1);
  const columnMap = mapColumnIndex(headers);
  
  // Check if we found a task name column
  if (columnMap.taskName === undefined) {
    // Try to use first column as task name
    columnMap.taskName = 0;
  }
  
  const hasDateColumns = columnMap.startDate !== undefined || columnMap.endDate !== undefined;
  
  const parsedRows: ParsedCSVRow[] = [];
  
  dataRows.forEach((row, rowIndex) => {
    const taskName = row[columnMap.taskName]?.trim();
    
    if (!taskName) {
      errors.push(`Row ${rowIndex + 2}: Missing task name`);
      return;
    }
    
    const parsed: ParsedCSVRow = {
      taskName,
    };
    
    // Intent
    if (columnMap.intent !== undefined) {
      parsed.intent = row[columnMap.intent]?.trim();
    }
    
    // Specs (can be semicolon-separated)
    if (columnMap.specs !== undefined) {
      const specsStr = row[columnMap.specs]?.trim();
      if (specsStr) {
        parsed.specs = specsStr.split(/[;|]/).map(s => s.trim()).filter(Boolean);
      }
    }
    
    // Duration
    if (columnMap.duration !== undefined) {
      parsed.duration = parseDuration(row[columnMap.duration]);
    }
    
    // Start date
    if (columnMap.startDate !== undefined) {
      parsed.startDate = parseDate(row[columnMap.startDate]);
    }
    
    // End date
    if (columnMap.endDate !== undefined) {
      parsed.endDate = parseDate(row[columnMap.endDate]);
    }
    
    // Fixed deadline
    if (columnMap.isFixedDeadline !== undefined) {
      const val = row[columnMap.isFixedDeadline]?.toLowerCase().trim();
      parsed.isFixedDeadline = ['true', 'yes', '1', 'x', 'fixed'].includes(val);
    }
    
    // Dependencies (comma or semicolon-separated task names)
    if (columnMap.dependencies !== undefined) {
      const depsStr = row[columnMap.dependencies]?.trim();
      if (depsStr) {
        parsed.dependencies = depsStr.split(/[,;]/).map(s => s.trim()).filter(Boolean);
      }
    }
    
    parsedRows.push(parsed);
  });
  
  return { rows: parsedRows, hasDateColumns, errors };
}

// Convert parsed CSV rows to Task objects with sequential scheduling
export function csvRowsToTasks(
  rows: ParsedCSVRow[],
  projectStartDate: Date,
  existingTasks: Task[] = []
): { tasks: Task[]; errors: string[] } {
  const tasks: Task[] = [];
  const errors: string[] = [];
  const taskNameToId: Record<string, string> = {};
  
  // Build map of existing task names to IDs
  existingTasks.forEach(t => {
    taskNameToId[t.name.toLowerCase()] = t.id;
  });
  
  let currentTime = new Date(projectStartDate);
  // Start at 9 AM if time is midnight
  if (currentTime.getHours() === 0) {
    currentTime.setHours(9, 0, 0, 0);
  }
  
  const DEFAULT_DURATION = 2 * 60; // 2 hours in minutes
  
  rows.forEach((row, index) => {
    const taskId = `task-${Date.now()}-${index}`;
    taskNameToId[row.taskName.toLowerCase()] = taskId;
    
    // Calculate duration
    const durationMinutes = row.duration || DEFAULT_DURATION;
    
    // Calculate start/end times
    let startTime: Date;
    let endTime: Date;
    
    if (row.startDate && row.endDate) {
      startTime = row.startDate;
      endTime = row.endDate;
    } else if (row.startDate) {
      startTime = row.startDate;
      endTime = new Date(startTime.getTime() + durationMinutes * 60000);
    } else {
      // Sequential scheduling
      startTime = new Date(currentTime);
      endTime = new Date(startTime.getTime() + durationMinutes * 60000);
      
      // Update current time for next task (add 30 min gap)
      currentTime = new Date(endTime.getTime() + 30 * 60000);
      
      // Skip to next day if past 6 PM
      if (currentTime.getHours() >= 18) {
        currentTime.setDate(currentTime.getDate() + 1);
        currentTime.setHours(9, 0, 0, 0);
      }
    }
    
    // Resolve dependencies
    const resolvedDeps: string[] = [];
    if (row.dependencies) {
      row.dependencies.forEach(depName => {
        const depId = taskNameToId[depName.toLowerCase()];
        if (depId) {
          resolvedDeps.push(depId);
        } else {
          errors.push(`Task "${row.taskName}": Dependency "${depName}" not found`);
        }
      });
    }
    
    // Build action items placeholder
    const actionItems: ActionItem[] = [
      { id: `ai-${taskId}-1`, text: 'Review requirements', completed: false },
    ];
    
    const task: Task = {
      id: taskId,
      name: row.taskName,
      brief: {
        intent: row.intent || `Complete ${row.taskName} as specified.`,
        specs: row.specs || [`Duration: ${Math.round(durationMinutes / 60 * 10) / 10} hours`],
        actionItems,
      },
      startTime,
      endTime,
      isFixedDeadline: row.isFixedDeadline || false,
      dependencies: resolvedDeps,
      progress: 0,
    };
    
    tasks.push(task);
  });
  
  return { tasks, errors };
}
